import { useState } from "react";

import usePDFDownload from "@Components/shared/hooks/usePDFDownload";
import useStyledPDF from "@Components/shared/hooks/useStyledPDF";
import SessionManager from "@Core/session";
import { IAssessmentInstanceDetails } from "@Models/IAssessmentDetails";
import { IPdfItem } from "@Models/IPdfItem";
import AssessmentService from "@Services/AssessmentService";
import { notification } from "antd";
import { useCreateAssessmentMetadata } from "../mutations";

const useAssessmentReport = () => {
    // ** State Hooks ** //
    const [active, setActive] = useState<Set<string>>(new Set<string>());

    // ** Hooks ** //
    const { mutateAsync: createMetadata, isLoading: isPreparing } = useCreateAssessmentMetadata();
    const download = usePDFDownload();
    const createStyledPDFReport = useStyledPDF({ report: "assessment", theme: "certificate" });

    // ** Variables ** //
    const service = new AssessmentService();
    const { userId } = SessionManager.getServiceUser();

    // ** Report ** //

    const prepareReport = async (assessmentInstanceDetails: IAssessmentInstanceDetails): Promise<void> => {
        if (active.has(assessmentInstanceDetails.assessmentInstanceId)) return;
        setActive((prev) => new Set([...prev, assessmentInstanceDetails.assessmentInstanceId]));

        // we don't use onSuccess here. it only runs for the final part of the mutation
        const creationResult = await createMetadata({
            req: {
                assessmentId: assessmentInstanceDetails.assessmentId,
                assessmentInstanceId: assessmentInstanceDetails.assessmentInstanceId,
            },
        });

        if (creationResult?.hasErrors) {
            removeFromActive(assessmentInstanceDetails.assessmentInstanceId);
            return notification.error({
                message: "Unable to download the assessment report.",
            });
        }

        const metadataId = creationResult?.value?.id;
        if (metadataId) {
            const result = await service.getReport(userId, assessmentInstanceDetails?.assessmentId, metadataId);
            if (result.hasErrors) {
                return notification.error({
                    message: "Unable to download the assessment report.",
                });
            }

            const reportContent = !result.hasErrors ? result.value : [];

            downloadReport(reportContent, assessmentInstanceDetails);
        }
    };

    const downloadReport = async (reportContent: IPdfItem[], assessmentInstanceDetails: IAssessmentInstanceDetails) => {
        const Report = () => (!!reportContent ? createStyledPDFReport({ content: reportContent }) : null);
        const fileName = `${assessmentInstanceDetails?.name} Report.pdf`;
        await download({ file: Report, fileName })
            .then((isSuccess) => {
                if (isSuccess)
                    notification.success({
                        message: `Downloaded: ${fileName}.`,
                    });
            })
            .catch((err) => {
                // eslint-disable-next-line no-console
                console.error(err);
                notification.error({
                    message: "Unable to view the assessment report.",
                });
            })
            .finally(() => {
                removeFromActive(assessmentInstanceDetails?.assessmentInstanceId);
            });
    };

    const removeFromActive = (assessmentInstanceId: string) => {
        setActive((prev) => {
            const newSet = new Set(prev);
            newSet.delete(assessmentInstanceId);
            return newSet;
        });
    };

    return {
        activeDownloads: active,
        isPreparing,
        prepareReport,
    };
};

export default useAssessmentReport;
